import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  TablePagination,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  Memory,
  Psychology,
  Save,
  ContentCopy
} from '@mui/icons-material';
import { middlewareDataAPI } from '../../services/api';
import { usePermissions } from '../../contexts/PermissionContext';
import { isImageFormat, isVideoFormat } from '../../config/outputFormatConfig';

const MiddlewareData = () => {
  const { hasPermission } = usePermissions();

  // Helper function to extract template variables from prompt template
  const extractTemplateVariables = (template) => {
    if (!template) return [];
    const regex = /\{([^}]+)\}/g;
    const variables = [];
    let match;
    while ((match = regex.exec(template)) !== null) {
      const variable = match[1].trim();
      if (!variables.includes(variable)) {
        variables.push(variable);
      }
    }
    return variables;
  };

  // COMMENTED OUT: Template Configuration Functions
  /*
  // Handle prompt template change and auto-extract variables
  const handlePromptTemplateChange = (template) => {
    const extractedVariables = extractTemplateVariables(template);
    
    setFormData(prev => ({
      ...prev,
      prompt_template: template,
      available_variables: extractedVariables
    }));
  };
  */
  // END COMMENTED OUT: Template Configuration Functions

  const canView = hasPermission('MiddlewareData', 'view');
  const canCreate = hasPermission('MiddlewareData', 'create');
  const canUpdate = hasPermission('MiddlewareData', 'update');
  const canDelete = hasPermission('MiddlewareData', 'delete');

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Validation states
  const [validationErrors, setValidationErrors] = useState({});

  // Data states
  const [modelRegistry, setModelRegistry] = useState([]);
  const [promptRegistry, setPromptRegistry] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modelTypes, setModelTypes] = useState([]);
  const [themes, setThemes] = useState([]);

  const [outputFormats, setOutputFormats] = useState([]);
  const [genderOptions, setGenderOptions] = useState([]);
  const [modelRegistryOptions, setModelRegistryOptions] = useState([]);

  // Filter states
  const [showInactiveOnly, setShowInactiveOnly] = useState(() => {
    // Get the saved preference from localStorage, default to false
    const saved = localStorage.getItem('middlewareData_showInactiveOnly');
    return saved ? JSON.parse(saved) : false;
  });

  const [promptFilters, setPromptFilters] = useState({
    search: '',
    model_type_id: '',
    is_model_face: ''
  });

  // Pagination states
  const [paginationState, setPaginationState] = useState({
    modelRegistry: { page: 0, rowsPerPage: 10 },
    promptRegistry: { page: 0, rowsPerPage: 10 }
  });

  // Total counts
  const [totalCounts, setTotalCounts] = useState({
    modelRegistry: 0,
    promptRegistry: 0
  });

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    is_active: true,
    is_model_face: false,
    sort_order: 0,
    // Prompt registry specific fields
    prompt_template: '',
    category: '',
    prompt_type: '',
    model_compatibility: [],
    gender_option_id: '',
    theme_id: '',

    // New field for prompt
    prompt_template: '',
    // Dictionary data field
    dictionary_data: '',
    // Model registry specific fields
    version: '',
    api_endpoint: '',
    fallback_api_endpoint: '',
    provider: '',
    credits: '',
    model_type: '',
    // Conditional fields based on model type
    guidance_scale: '',
    safety_tolerance: '',
    duration: '',
    seed: '',
    aspect_ratio: '9:16',
    resolution: '2k', // Default resolution for nano-banana-pro
    camera_fixed: false, // For I2V models - always false
    generate_audio: false, // For I2V models - always false
    camera_fixed: false, // For I2V models - always false
    generate_audio: false, // For I2V models - always false
  });

  // Prompt input state
  const [inputPrompt, setInputPrompt] = useState('');

  useEffect(() => {
    // Load all data on component mount
    loadAllData();

    // Load static options once on mount
    loadCategories(); // Load categories once on mount

    loadOutputFormats(); // Load output formats once on mount
    loadGenderOptions(); // Load gender options once on mount
    loadModelTypes(); // Load all model types once on mount
    loadThemes(); // Load themes once on mount
    loadModelRegistryOptions(); // Load model registry options once on mount
  }, []);

  // Load data when tab changes or pagination changes
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (activeTab === 0) loadModelRegistry();
      else if (activeTab === 1) loadPromptRegistry();
    }, 100);

    return () => clearTimeout(debounceTimer);
  }, [
    activeTab,
    paginationState.modelRegistry.page,
    paginationState.modelRegistry.rowsPerPage,
    paginationState.promptRegistry.page,
    paginationState.promptRegistry.rowsPerPage,
    showInactiveOnly,
    promptFilters.search,
    promptFilters.model_type_id,
    promptFilters.is_model_face
  ]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      if (activeTab === 0) await loadModelRegistry();
      else if (activeTab === 1) await loadPromptRegistry();
    } catch (error) {
      console.error('Failed to load data:', error);
      setError('Failed to load middleware data');
    } finally {
      setLoading(false);
    }
  };

  const loadModelRegistry = async () => {
    try {
      const params = {
        page: paginationState.modelRegistry.page + 1,
        per_page: paginationState.modelRegistry.rowsPerPage,
        show_inactive_only: showInactiveOnly
      };
      const response = await middlewareDataAPI.getModelRegistry(params);
      setModelRegistry(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, modelRegistry: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load model registry:', error);
      setError('Failed to load model registry');
    }
  };

  const loadPromptRegistry = async () => {
    try {
      const params = {
        page: paginationState.promptRegistry.page + 1,
        per_page: paginationState.promptRegistry.rowsPerPage,
        show_inactive_only: showInactiveOnly,
        search: promptFilters.search,
        model_type_id: promptFilters.model_type_id,
        is_model_face: promptFilters.is_model_face
      };
      const response = await middlewareDataAPI.getPromptRegistry(params);
      setPromptRegistry(response.data.data || []);
      setTotalCounts(prev => ({ ...prev, promptRegistry: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load prompt registry:', error);
      setError('Failed to load prompt registry');
    }
  };

  const loadCategories = async () => {
    try {
      const response = await middlewareDataAPI.getCategories();
      setCategories(response.data.data || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
      // Don't set error state for categories as it's not critical
    }
  };

  const loadModelTypes = async () => {
    try {
      const response = await middlewareDataAPI.getModelTypes();
      setModelTypes(response.data.data || []);
    } catch (error) {
      console.error('Failed to load model types:', error);
      // Don't set error state for model types as it's not critical
    }
  };



  const loadOutputFormats = async () => {
    try {
      const response = await middlewareDataAPI.getOutputFormats();
      setOutputFormats(response.data.data || []);
    } catch (error) {
      console.error('Failed to load output formats:', error);
      // Don't set error state for output formats as it's not critical
    }
  };

  const loadGenderOptions = async () => {
    try {
      const response = await middlewareDataAPI.getGenderOptions();
      setGenderOptions(response.data.data || []);
    } catch (error) {
      console.error('Failed to load gender options:', error);
    }
  };

  const loadThemes = async () => {
    try {
      const response = await middlewareDataAPI.getThemes();
      setThemes(response.data.data || []);
    } catch (error) {
      console.error('Failed to load themes:', error);
    }
  };



  const loadModelRegistryOptions = async () => {
    try {
      const response = await middlewareDataAPI.getModelRegistryDropdown();
      setModelRegistryOptions(response.data.data || []);
    } catch (error) {
      console.error('Failed to load model registry options:', error);
      // Don't set error state for model registry options as it's not critical
    }
  };

  const handleOutputFormatChange = (outputFormatId) => {
    // Find the selected output format to check if it's video
    const selectedFormat = outputFormats.find(format => format.id === outputFormatId);
    const isVideoFormat = selectedFormat && (
      (selectedFormat.name && selectedFormat.name.toLowerCase().includes('video')) ||
      (selectedFormat.display_name && selectedFormat.display_name.toLowerCase().includes('video'))
    );

    const prevOutputFormatId = formData.output_format_id;

    setFormData(prev => {
      // Only reset fields if output format actually changed
      if (outputFormatId !== prevOutputFormatId) {
        // Check if we're in edit mode
        if (editingItem !== null) {
          // In edit mode, preserve existing selections
          console.log('🔄 Output Format changed during edit, preserving existing selections...');
          return {
            ...prev,
            output_format_id: outputFormatId,
            // Keep existing video quality if switching to video format

          };
        } else {
          // In create mode, reset dependent fields
          return {
            ...prev,
            output_format_id: outputFormatId,

          };
        }
      } else {
        // Output format didn't change, preserve all values
        return {
          ...prev,
          output_format_id: outputFormatId
        };
      }
    });
  };

  const getTabData = (tabIndex) => {
    switch (tabIndex) {
      case 0: return {
        data: modelRegistry,
        type: 'model_registry',
        columns: [
          { id: 'name', label: 'Name' },
          { id: 'display_name', label: 'Display Name' },
          { id: 'version', label: 'Version' },
          { id: 'provider', label: 'Provider' },
          { id: 'model_type', label: 'Model Type' },
          { id: 'api_endpoint', label: 'API Endpoint' },
          { id: 'fallback_api_endpoint', label: 'Fallback Endpoint' },
          { id: 'is_active', label: 'Active', type: 'boolean' },
          { id: 'sort_order', label: 'Order' }
        ],
        title: 'Model Registry'
      };
      case 1: return {
        data: promptRegistry,
        type: 'prompt_registry',
        columns: [
          { id: 'name', label: 'Name' },
          { id: 'display_name', label: 'Display Name' },
          { id: 'category', label: 'Category', format: (value, row) => row.category ? row.category.display_name : '-' },
          { id: 'model_type', label: 'Model Type', format: (value, row) => row.model_type ? row.model_type.display_name : '-' },

          { id: 'output_format', label: 'Output Format', format: (value, row) => row.output_format ? row.output_format.display_name : '-' },
          { id: 'theme', label: 'Theme', format: (value, row) => row.theme ? row.theme.display_name : '-' },

          { id: 'prompt_type', label: 'Prompt Type' },
          { id: 'version', label: 'Version' },
          {
            id: 'model_compatibility', label: 'Compatible Models', format: (value) => {
              if (!value) return '-';
              if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '-';
              if (typeof value === 'string') return value.trim() || '-';
              return '-';
            }
          },
          { id: 'is_active', label: 'Active', type: 'boolean' },
          { id: 'sort_order', label: 'Order' }
        ],
        title: 'Prompt Registry'
      };
      default: return { data: [], type: '', columns: [], title: '' };
    }
  };

  const getTabKey = (dialogType) => {
    switch (dialogType) {
      case 'model_registry': return 'modelRegistry';
      case 'prompt_registry': return 'promptRegistry';
      default: return '';
    }
  };

  const renderFormFields = (type) => {
    // No longer checking if we're editing a prompt registry item

    const commonFields = (
      <>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="Name"
            value={formData.name || ''}
            onChange={(e) => {
              setFormData({ ...formData, name: e.target.value });
            }}
            error={!!validationErrors.name}
            helperText={validationErrors.name}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="Display Name"
            value={formData.display_name || ''}
            onChange={(e) => {
              setFormData({ ...formData, display_name: e.target.value });
            }}
            error={!!validationErrors.display_name}
            helperText={validationErrors.display_name}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Sort Order"
            value={formData.sort_order || 0}
            onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
            inputProps={{ min: 0 }}
          />
        </Grid>
        <FormControlLabel
          sx={{ ml: 0 }}
          control={
            <Switch
              checked={formData.is_active !== undefined ? formData.is_active : true}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              color="primary"
            />
          }
          label={<Typography variant="body2">Active</Typography>}
        />
      </>
    );

    switch (type) {
      case 'model_registry':
        return (
          <>
            {commonFields}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Version"
                value={formData.version || ''}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Provider"
                value={formData.provider || ''}
                onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                error={!!validationErrors.provider}
                helperText={validationErrors.provider}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Credits"
                value={formData.credits || ''}
                onChange={(e) => setFormData({ ...formData, credits: e.target.value })}
                inputProps={{ min: 0, step: 1 }}
                helperText={validationErrors.credits || "Number of credits required for this model"}
                error={!!validationErrors.credits}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!validationErrors.model_type}>
                <InputLabel>Model Type</InputLabel>
                <Select
                  value={formData.model_type || ''}
                  onChange={(e) => setFormData({ ...formData, model_type: e.target.value })}
                  label="Model Type"
                >
                  <MenuItem value="">
                    <em>Select Model Type</em>
                  </MenuItem>
                  <MenuItem value="I2I">I2I (Image to Image)</MenuItem>
                  <MenuItem value="I2V">I2V (Image to Video)</MenuItem>
                </Select>
                {validationErrors.model_type && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {validationErrors.model_type}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Conditional fields based on model type */}
            {formData.model_type === 'I2I' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Guidance Scale"
                    value={formData.guidance_scale || ''}
                    onChange={(e) => setFormData({ ...formData, guidance_scale: e.target.value })}
                    inputProps={{ min: 0, step: 0.1 }}
                    helperText={validationErrors.guidance_scale || "Controls how closely the model follows the prompt"}
                    error={!!validationErrors.guidance_scale}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Safety Tolerance"
                    value={formData.safety_tolerance || ''}
                    onChange={(e) => setFormData({ ...formData, safety_tolerance: e.target.value })}
                    inputProps={{ min: 0, step: 0.1 }}
                    helperText={validationErrors.safety_tolerance || "Controls content safety filtering"}
                    error={!!validationErrors.safety_tolerance}
                  />
                </Grid>
              </>
            )}

            {formData.model_type === 'I2V' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Duration"
                    value={formData.duration || ''}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    inputProps={{ min: 1, step: 1 }}
                    helperText={validationErrors.duration || "Duration in seconds"}
                    error={!!validationErrors.duration}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Seed"
                    value={formData.seed || ''}
                    onChange={(e) => setFormData({ ...formData, seed: e.target.value })}
                    inputProps={{ min: 0, step: 1 }}
                    helperText={validationErrors.seed || "Random seed for consistent results"}
                    error={!!validationErrors.seed}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Endpoint"
                value={formData.api_endpoint || ''}
                onChange={(e) => setFormData({ ...formData, api_endpoint: e.target.value })}
                error={!!validationErrors.api_endpoint}
                helperText={validationErrors.api_endpoint}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Fallback API Endpoint (Failsafe / GPT)"
                value={formData.fallback_api_endpoint || ''}
                onChange={(e) => setFormData({ ...formData, fallback_api_endpoint: e.target.value })}
                helperText="Used when failsafe is in FALLBACK (e.g. https://api.openai.com/v1/images/edits). Overrides SECONDARY_PROVIDER_ENDPOINT env for this model."
              />
            </Grid>
          </>
        );

      case 'prompt_registry':
        return (
          <>
            {commonFields}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!validationErrors.category_id}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category_id || ''}
                  onChange={(e) => {
                    const newCategoryId = e.target.value;
                    const prevCategoryId = formData.category_id;

                    setFormData(prev => {
                      // Only reset fields if category actually changed
                      if (newCategoryId !== prevCategoryId) {
                        // Check if we're in edit mode
                        if (editingItem !== null) {
                          // In edit mode, preserve existing selections
                          console.log('🔄 Category changed during edit, preserving existing selections...');
                          return {
                            ...prev,
                            category_id: newCategoryId,
                            // Keep existing values for other fields
                          };
                        } else {
                          // In create mode, reset all dependent fields
                          return {
                            ...prev,
                            category_id: newCategoryId,
                            model_type_id: '', // Reset model type when category changes

                            output_format_id: '', // Reset output format when category changes

                          };
                        }
                      } else {
                        // Category didn't change, preserve all values
                        return {
                          ...prev,
                          category_id: newCategoryId
                        };
                      }
                    });
                  }}
                  label="Category"
                >
                  <MenuItem value="">
                    <em>-- Select Category --</em>
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.display_name}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.category_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {validationErrors.category_id}
                  </Typography>
                )}

              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!validationErrors.model_type_id}>
                <InputLabel>Model Type</InputLabel>
                <Select
                  value={formData.model_type_id || ''}
                  onChange={(e) => {
                    const newModelTypeId = e.target.value;
                    const prevModelTypeId = formData.model_type_id;

                    setFormData(prev => {
                      // Only reset fields if model type actually changed
                      if (newModelTypeId !== prevModelTypeId) {
                        // Check if we're in edit mode
                        if (editingItem !== null) {
                          // In edit mode, preserve existing selections
                          console.log('🔄 Model Type changed during edit, preserving existing selections...');
                          return {
                            ...prev,
                            model_type_id: newModelTypeId,
                            // Keep existing values for other fields
                          };
                        } else {
                          // In create mode, reset dependent fields
                          return {
                            ...prev,
                            model_type_id: newModelTypeId,

                            output_format_id: '', // Reset output format when model type changes

                          };
                        }
                      } else {
                        // Model type didn't change, preserve all values
                        return {
                          ...prev,
                          model_type_id: newModelTypeId
                        };
                      }
                    });
                  }}
                  label="Model Type"
                >
                  <MenuItem value="">
                    <em>-- Select Model Type --</em>
                  </MenuItem>
                  {modelTypes.map((modelType) => (
                    <MenuItem key={modelType.id} value={modelType.id}>
                      {modelType.display_name}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.model_type_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {validationErrors.model_type_id}
                  </Typography>
                )}
              </FormControl>

            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Gender (Optional)</InputLabel>
                <Select
                  value={formData.gender_option_id || ''}
                  onChange={(e) => setFormData({ ...formData, gender_option_id: e.target.value })}
                  label="Gender (Optional)"
                >
                  <MenuItem value="">
                    <em>-- General (No Gender) --</em>
                  </MenuItem>
                  {genderOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.display_name}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                  Optional. Select to map this prompt specifically to a gender.
                </Typography>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Theme (Optional)</InputLabel>
                <Select
                  value={formData.theme_id || ''}
                  onChange={(e) => setFormData({ ...formData, theme_id: e.target.value })}
                  label="Theme (Optional)"
                >
                  <MenuItem value="">
                    <em>-- No Theme --</em>
                  </MenuItem>
                  {themes.map((theme) => (
                    <MenuItem key={theme.id} value={theme.id}>
                      {theme.display_name}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                  Optional. Select to map this prompt to a specific theme (e.g., for Product Only model type).
                </Typography>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!validationErrors.output_format_id}>
                <InputLabel>Output Format</InputLabel>
                <Select
                  value={formData.output_format_id || ''}
                  onChange={(e) => {
                    handleOutputFormatChange(e.target.value);
                  }}
                  label="Output Format"
                >
                  <MenuItem value="">
                    <em>-- Select Output Format --</em>
                  </MenuItem>
                  {outputFormats.map((format) => (
                    <MenuItem key={format.id} value={format.id}>
                      {format.display_name}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.output_format_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {validationErrors.output_format_id}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Aspect Ratio</InputLabel>
                <Select
                  value={formData.aspect_ratio || '9:16'}
                  onChange={(e) => setFormData({ ...formData, aspect_ratio: e.target.value })}
                  label="Aspect Ratio"
                >
                  <MenuItem value="1:1">1:1 (Square)</MenuItem>
                  <MenuItem value="16:9">16:9 (Landscape)</MenuItem>
                  <MenuItem value="9:16">9:16 (Portrait)</MenuItem>
                  <MenuItem value="4:5">4:5 (Portrait)</MenuItem>
                  <MenuItem value="4:3">4:3 (Standard)</MenuItem>
                  <MenuItem value="3:4">3:4 (Portrait)</MenuItem>
                  <MenuItem value="3:2">3:2 (Classic)</MenuItem>
                  <MenuItem value="2:3">2:3 (Portrait)</MenuItem>
                  <MenuItem value="21:9">21:9 (Ultrawide)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Resolution</InputLabel>
                <Select
                  value={formData.resolution || '2k'}
                  onChange={(e) => setFormData({ ...formData, resolution: e.target.value })}
                  label="Resolution"
                >
                  <MenuItem value="1k">1K</MenuItem>
                  <MenuItem value="2k">2K (Default)</MenuItem>
                  <MenuItem value="4k">4K</MenuItem>
                  <MenuItem value="480p">480p</MenuItem>
                  <MenuItem value="720p">720p</MenuItem>
                  <MenuItem value="1080p">1080p</MenuItem>
                  <MenuItem value="480p">480p</MenuItem>
                  <MenuItem value="720p">720p</MenuItem>
                  <MenuItem value="1080p">1080p</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Camera Fixed & Generate Audio - shown for Video format, always false */}
            {(() => {
              const output_format_id = formData.output_format_id;
              const isVideo = isVideoFormat(output_format_id);

              if (!isVideo) return null;

              return (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.camera_fixed || false}
                          onChange={(e) => setFormData({ ...formData, camera_fixed: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="Camera Fixed"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1.5 }}>
                      Whether the camera should remain fixed during video generation
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.generate_audio || false}
                          onChange={(e) => setFormData({ ...formData, generate_audio: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="Generate Audio"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1.5 }}>
                      Whether to generate audio along with the video
                    </Typography>
                  </Grid>
                </>
              );
            })()}

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Checkbox
                    checked={formData.is_model_face || false}
                    onChange={(e) => setFormData({ ...formData, is_model_face: e.target.checked })}
                    color="secondary"
                  />
                }
                label={<Typography variant="body2">Model Face Prompt</Typography>}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                Check if this prompt is specifically for model-face generation.
              </Typography>
            </Grid>

            {/* Dictionary Data field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Dictionary Data (JSON)"
                value={formData.dictionary_data || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({ ...formData, dictionary_data: value });
                }}
                onBlur={(e) => {
                  // Try to format JSON when user leaves the field
                  const value = e.target.value.trim();
                  if (value) {
                    try {
                      const parsed = JSON.parse(value);
                      const formatted = JSON.stringify(parsed, null, 2);
                      setFormData(prev => ({ ...prev, dictionary_data: formatted }));
                    } catch (e) {
                      // If parsing fails, keep the original value
                      console.warn('Invalid JSON format:', e);
                    }
                  }
                }}
                placeholder='{"key1": "value1", "key2": "value2"}'
                helperText="Enter dictionary data in JSON format. This will be stored in the dictionary_data table."
                error={!!validationErrors.dictionary_data}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    if (formData.dictionary_data) {
                      try {
                        const parsed = JSON.parse(formData.dictionary_data);
                        const formatted = JSON.stringify(parsed, null, 2);
                        setFormData(prev => ({ ...prev, dictionary_data: formatted }));
                      } catch (e) {
                        setError('Invalid JSON format. Please check your input.');
                      }
                    }
                  }}
                >
                  Format JSON
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    if (formData.dictionary_data) {
                      try {
                        const parsed = JSON.parse(formData.dictionary_data);
                        const minified = JSON.stringify(parsed);
                        setFormData(prev => ({ ...prev, dictionary_data: minified }));
                      } catch (e) {
                        setError('Invalid JSON format. Please check your input.');
                      }
                    }
                  }}
                >
                  Minify JSON
                </Button>
              </Box>
              {validationErrors.dictionary_data && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {validationErrors.dictionary_data}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!validationErrors.prompt_type}>
                <InputLabel>Prompt Type</InputLabel>
                <Select
                  value={formData.prompt_type || ''}
                  onChange={(e) => {
                    setFormData({ ...formData, prompt_type: e.target.value });
                  }}
                  label="Prompt Type"
                >
                  <MenuItem value="">
                    <em>-- Select Prompt Type --</em>
                  </MenuItem>
                  <MenuItem value="I2I">I2I</MenuItem>
                  <MenuItem value="I2V">I2V</MenuItem>
                </Select>
                {validationErrors.prompt_type && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {validationErrors.prompt_type}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Version"
                value={formData.version || ''}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                placeholder="e.g., 1.0, 2.1, beta"
              />
            </Grid>

            {/* Guidance Scale field - Hidden for Video format */}
            {(() => {
              // Check by ID using configuration constants
              const output_format_id = formData.output_format_id;
              const isVideo = isVideoFormat(output_format_id);

              // Hide guidance scale field for Video format
              if (isVideo) {
                return null;
              }

              return (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Guidance Scale"
                    value={formData.guidance_scale || ''}
                    onChange={(e) => setFormData({ ...formData, guidance_scale: parseFloat(e.target.value) || '' })}
                    inputProps={{ min: 0, step: 0.1 }}
                    helperText={validationErrors.guidance_scale || "Controls how closely the model follows the prompt (default: 18.4)"}
                    error={!!validationErrors.guidance_scale}
                  />
                </Grid>
              );
            })()}

            {/* Prompt Parsing Section - Prompt (Hidden for Video format) */}
            {(() => {
              // Check by ID using configuration constants
              const output_format_id = formData.output_format_id;
              const isVideo = isVideoFormat(output_format_id);

              // Hide prompt field for Video format
              if (isVideo) {
                return null;
              }

              return (
                <Grid item xs={12}>
                  <Box sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2, backgroundColor: '#f0f9ff' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: '600', color: 'black' }}>
                        🎯 Prompt
                      </Typography>
                    </Box>

                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#6b7280', mb: 2 }}>
                      Have a prompt? Paste it below and we'll automatically detect the attributes and populate the form fields for you.
                    </Typography>

                    {/* Input Field */}
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Prompt"
                        placeholder='Example: "Create Bust image of Necklace made from Gold with Female model"'
                        value={inputPrompt}
                        onChange={(e) => {
                          setInputPrompt(e.target.value);
                        }}
                        error={!!validationErrors.processed_prompt}
                        helperText={validationErrors.processed_prompt}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Box>

                    {/* Parse Button - HIDDEN */}
                    {/* 
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Button
                        variant="contained"
                        onClick={parseAndPopulatePrompt}
                        disabled={!inputPrompt.trim() || promptParsing || editingItem}
                        sx={{
                          backgroundColor: editingItem ? '#9ca3af' : '#3b82f6',
                          '&:hover': {
                            backgroundColor: editingItem ? '#9ca3af' : '#F2CC00'
                          },
                          textTransform: 'none',
                          fontWeight: '600'
                        }}
                      >
                        {promptParsing ? 'Parsing...' : editingItem ? 'Parse & Auto-Fill (Disabled in Edit Mode)' : 'Parse & Auto-Fill'}
                      </Button>
                      

                    </Box>
                    */}


                  </Box>
                </Grid>
              );
            })()}

            {/* COMMENTED OUT: Prompt Template Configuration Feature
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Prompt Template"
                value={formData.prompt_template || ''}
                onChange={(e) => handlePromptTemplateChange(e.target.value)}
                placeholder="Enter prompt template with placeholders like {category}, {style}, etc."
                helperText="Use curly braces {} to define variables. They will be automatically detected and can be configured below."
              />
            </Grid>
            
            {/* Available Attributes Helper */}
            {/* <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" gutterBottom>
                  💡 Available System Attributes
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  You can use these attribute variables in your template. They will be automatically replaced with the user's selections:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {[
                    'category', 'model_type', 'background_type', 
                    'bust_color', 'gender', 'skin_tone', 'output_format'
                  ].map((attr) => (
                    <Chip
                      key={attr}
                      label={`{${attr}}`}
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const currentTemplate = formData.prompt_template || '';
                        const newTemplate = currentTemplate + `{${attr}}`;
                        handlePromptTemplateChange(newTemplate);
                      }}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Click any attribute above to add it to your template, or type manually with curly braces.
                </Typography>
              </Box>
            </Grid>
            
            {/* Detected Variables (Read-only) */}
            {/* {formData.available_variables && formData.available_variables.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    🔍 Detected Variables
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    These variables will be automatically replaced with user selections during generation:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {formData.available_variables.map((variable) => (
                      <Chip
                        key={variable}
                        label={`{${variable}}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>
            )} */}
            {/* END COMMENTED OUT: Prompt Template Configuration Feature */}

            <Grid item xs={12}>
              <FormControl fullWidth error={!!validationErrors.model_compatibility}>
                <InputLabel>Model Compatibility</InputLabel>
                <Select
                  multiple
                  value={Array.isArray(formData.model_compatibility) ? formData.model_compatibility : []}
                  onChange={(e) => {
                    setFormData({ ...formData, model_compatibility: e.target.value });
                  }}
                  label="Model Compatibility"
                  renderValue={(selected) => {
                    if (!Array.isArray(selected) || selected.length === 0) {
                      return 'Select compatible models';
                    }
                    const selectedModels = modelRegistryOptions.filter(model =>
                      selected.includes(model.name)
                    );
                    return selectedModels.map(model => model.display_name).join(', ');
                  }}
                >
                  {modelRegistryOptions.map((model) => (
                    <MenuItem key={model.id} value={model.name}>
                      <Checkbox
                        checked={formData.model_compatibility?.includes(model.name) || false}
                        size="small"
                      />
                      <Box display="flex" flexDirection="column" alignItems="flex-start" sx={{ ml: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {model.display_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {model.provider} • {model.name}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.model_compatibility && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {validationErrors.model_compatibility}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </>
        );

      default:
        return commonFields;
    }
  };

  // Validation function for model registry
  const validateModelRegistry = () => {
    const errors = {};

    // Required field validations for both create and edit modes
    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Name is required';
    }

    if (!formData.display_name || formData.display_name.trim() === '') {
      errors.display_name = 'Display Name is required';
    }

    if (!formData.model_type || formData.model_type.trim() === '') {
      errors.model_type = 'Model Type is required';
    }

    if (!formData.api_endpoint || formData.api_endpoint.trim() === '') {
      errors.api_endpoint = 'API Endpoint is required';
    }

    if (!formData.provider || formData.provider.trim() === '') {
      errors.provider = 'Provider is required';
    }

    if (!formData.credits || formData.credits === '') {
      errors.credits = 'Credits is required';
    }

    // Validate credits is a positive number
    if (formData.credits && (isNaN(formData.credits) || parseFloat(formData.credits) < 0)) {
      errors.credits = 'Credits must be a positive number';
    }

    // Validate conditional fields based on model type
    if (formData.model_type === 'I2I') {
      if (formData.guidance_scale && (isNaN(formData.guidance_scale) || parseFloat(formData.guidance_scale) < 0)) {
        errors.guidance_scale = 'Guidance Scale must be a positive number';
      }
      if (formData.safety_tolerance && (isNaN(formData.safety_tolerance) || parseFloat(formData.safety_tolerance) < 0)) {
        errors.safety_tolerance = 'Safety Tolerance must be a positive number';
      }
    }

    if (formData.model_type === 'I2V') {
      if (formData.duration && (isNaN(formData.duration) || parseInt(formData.duration) < 1)) {
        errors.duration = 'Duration must be at least 1 second';
      }
      if (formData.seed && (isNaN(formData.seed) || parseInt(formData.seed) < 0)) {
        errors.seed = 'Seed must be a non-negative number';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validation function for prompt registry
  const validatePromptRegistry = () => {
    const errors = {};

    // Required field validations for both create and edit modes
    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Name is required';
    }

    if (!formData.display_name || formData.display_name.trim() === '') {
      errors.display_name = 'Display Name is required';
    }

    if (!formData.category_id) {
      errors.category_id = 'Category is required';
    }

    if (!formData.model_type_id) {
      errors.model_type_id = 'Model Type is required';
    }

    if (!formData.output_format_id) {
      errors.output_format_id = 'Output Format is required';
    }

    if (!formData.prompt_type || formData.prompt_type.trim() === '') {
      errors.prompt_type = 'Prompt Type is required';
    }

    // Prompt is required only for non-Video formats
    if (!isVideoFormat(formData.output_format_id)) { // Not Video format
      if (!inputPrompt || inputPrompt.trim() === '') {
        errors.processed_prompt = 'Prompt is required';
      }
    }

    if (!formData.model_compatibility || formData.model_compatibility.length === 0) {
      errors.model_compatibility = 'Model Compatibility is required';
    }

    // Validate dictionary_data if output format is Image or Video
    if (formData.output_format_id) {
      // Check by ID using configuration constants
      const output_format_id = formData.output_format_id;
      const isImage = isImageFormat(output_format_id);
      const isVideo = isVideoFormat(output_format_id);

      if (isImage || isVideo) {
        // Dictionary data is required for both Image and Video formats
        if (!formData.dictionary_data || formData.dictionary_data.trim() === '') {
          errors.dictionary_data = 'Dictionary data is required for Image and Video formats';
        } else {
          // Validate JSON format
          try {
            JSON.parse(formData.dictionary_data);
          } catch (e) {
            errors.dictionary_data = 'Dictionary data must be valid JSON format';
          }
        }
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!canCreate) {
      setError('You do not have permission to create items');
      return;
    }

    // Validate form based on dialog type
    if (dialogType === 'model_registry') {
      if (!validateModelRegistry()) {
        setError('Fill all required fields below');
        return;
      }
    } else if (dialogType === 'prompt_registry') {
      if (!validatePromptRegistry()) {
        setError('Fill all required fields below');
        return;
      }
    }

    try {
      const apiMethod = dialogType === 'model_registry'
        ? middlewareDataAPI.createModelRegistry
        : middlewareDataAPI.createPromptRegistry;

      // Clean form data before sending - convert empty strings to null for foreign keys
      const cleanedFormData = { ...formData };

      if (dialogType === 'model_registry') {
        // Convert empty strings to null for integer and float fields
        const integerFields = ['credits', 'sort_order', 'duration', 'seed'];
        const floatFields = ['guidance_scale', 'safety_tolerance'];

        integerFields.forEach(field => {
          if (cleanedFormData[field] === '' || cleanedFormData[field] === 0) {
            cleanedFormData[field] = null;
          }
        });

        floatFields.forEach(field => {
          if (cleanedFormData[field] === '' || cleanedFormData[field] === 0) {
            cleanedFormData[field] = null;
          }
        });
      } else if (dialogType === 'prompt_registry') {
        // Sync inputPrompt to prompt_template (only for non-Video formats)
        if (!isVideoFormat(formData.output_format_id) && inputPrompt.trim() !== '') { // Not Video format
          cleanedFormData.prompt_template = inputPrompt.trim();
        }

        // Handle dictionary_data field - validate JSON if provided
        if (cleanedFormData.dictionary_data && cleanedFormData.dictionary_data.trim() !== '') {
          try {
            const parsed = JSON.parse(cleanedFormData.dictionary_data);
            // Re-format the JSON to ensure consistent formatting
            cleanedFormData.dictionary_data = JSON.stringify(parsed, null, 2);
          } catch (e) {
            setError('Dictionary data must be valid JSON format');
            return;
          }
        }

        // Foreign key fields that should be null instead of empty string
        const foreignKeyFields = [
          'category_id', 'model_type_id',
          'output_format_id', 'gender_option_id', 'theme_id'
        ];

        foreignKeyFields.forEach(field => {
          if (cleanedFormData[field] === '' || cleanedFormData[field] === 0) {
            cleanedFormData[field] = null;
          }
        });
      }

      console.log('🚀 Sending data to backend (CREATE):', cleanedFormData);
      await apiMethod(cleanedFormData);
      setDialogOpen(false);
      resetFormData();

      // Navigate to first page to show new item
      const tabKey = getTabKey(dialogType);
      setPaginationState(prev => ({
        ...prev,
        [tabKey]: { ...prev[tabKey], page: 0 }
      }));

      loadAllData();
      setSuccess('Record created successfully');
    } catch (error) {
      console.error('Failed to create item:', error);
      const errorMessage = error.response?.data?.error || `Failed to create ${dialogType.replace('_', ' ')}`;

      // Check if it's a "Name already exists" validation error
      const isNameExistsError = errorMessage.toLowerCase().includes('name already exists') ||
        errorMessage.toLowerCase().includes('name already exist') ||
        errorMessage.toLowerCase().includes('already exists');

      if (isNameExistsError) {
        // For name validation errors, keep the dialog open and show error at top
        setError(errorMessage);
        // Don't close dialog or reset form data
      } else {
        // For other errors, close dialog and reset form
        setError(errorMessage);
        setDialogOpen(false);
      }
    }
  };

  const handleUpdate = async () => {
    if (!canUpdate) {
      setError('You do not have permission to update items');
      return;
    }

    // Validate form based on dialog type
    if (dialogType === 'model_registry') {
      if (!validateModelRegistry()) {
        setError('Fill all required fields below');
        return;
      }
    } else if (dialogType === 'prompt_registry') {
      if (!validatePromptRegistry()) {
        setError('Fill all required fields below');
        return;
      }
    }

    try {
      const apiMethod = dialogType === 'model_registry'
        ? middlewareDataAPI.updateModelRegistry
        : middlewareDataAPI.updatePromptRegistry;

      // Clean form data before sending - convert empty strings to null for foreign keys
      const cleanedFormData = { ...formData };

      if (dialogType === 'model_registry') {
        // Convert empty strings to null for integer and float fields
        const integerFields = ['credits', 'sort_order', 'duration', 'seed'];
        const floatFields = ['guidance_scale', 'safety_tolerance'];

        integerFields.forEach(field => {
          if (cleanedFormData[field] === '' || cleanedFormData[field] === 0) {
            cleanedFormData[field] = null;
          }
        });

        floatFields.forEach(field => {
          if (cleanedFormData[field] === '' || cleanedFormData[field] === 0) {
            cleanedFormData[field] = null;
          }
        });
      } else if (dialogType === 'prompt_registry') {
        // Sync inputPrompt to prompt_template (only for non-Video formats)
        if (!isVideoFormat(formData.output_format_id) && inputPrompt.trim() !== '') { // Not Video format
          cleanedFormData.prompt_template = inputPrompt.trim();
        }

        // Handle dictionary_data field - validate JSON if provided
        if (cleanedFormData.dictionary_data && cleanedFormData.dictionary_data.trim() !== '') {
          try {
            const parsed = JSON.parse(cleanedFormData.dictionary_data);
            // Re-format the JSON to ensure consistent formatting
            cleanedFormData.dictionary_data = JSON.stringify(parsed, null, 2);
          } catch (e) {
            setError('Dictionary data must be valid JSON format');
            return;
          }
        }

        // Foreign key fields that should be null instead of empty string
        const foreignKeyFields = [
          'category_id', 'model_type_id',
          'output_format_id', 'gender_option_id'
        ];

        foreignKeyFields.forEach(field => {
          if (cleanedFormData[field] === '' || cleanedFormData[field] === 0) {
            cleanedFormData[field] = null;
          }
        });
      }

      console.log('🚀 Sending data to backend (UPDATE):', cleanedFormData);
      await apiMethod(editingItem.id, cleanedFormData);
      setDialogOpen(false);
      setEditingItem(null);
      resetFormData();

      // Force reload data to ensure we get the latest dictionary data
      console.log('🔄 Reloading data after update...');
      // Add a small delay to ensure backend has processed the update
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadAllData();
      console.log('✅ Data reloaded after update');

      // Debug: Check if the updated data is now showing the new dictionary data
      if (dialogType === 'prompt_registry') {
        const updatedItem = promptRegistry.find(item => item.id === editingItem.id);
        if (updatedItem) {
          console.log('🔍 Updated item found:', updatedItem);
          console.log('🔍 Updated dictionary_data:', updatedItem.dictionary_data);
        } else {
          console.log('🔍 Updated item not found in promptRegistry');
        }
      }

      setSuccess('Record updated successfully');
    } catch (error) {
      console.error('Failed to update item:', error);
      const errorMessage = error.response?.data?.error || `Failed to update ${dialogType.replace('_', ' ')}`;

      // Check if it's a "Name already exists" validation error
      const isNameExistsError = errorMessage.toLowerCase().includes('name already exists') ||
        errorMessage.toLowerCase().includes('name already exist') ||
        errorMessage.toLowerCase().includes('already exists');

      if (isNameExistsError) {
        // For name validation errors, keep the dialog open and show error at top
        setError(errorMessage);
        // Don't close dialog or reset form data
      } else {
        // For other errors, close dialog and reset form
        setError(errorMessage);
        setDialogOpen(false);
      }
    }
  };

  const handleDelete = (item, type) => {
    setItemToDelete({ item, type });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!canDelete) {
      setError('You do not have permission to delete items');
      return;
    }

    if (itemToDelete) {
      try {
        const apiMethod = itemToDelete.type === 'model_registry'
          ? middlewareDataAPI.deleteModelRegistry
          : middlewareDataAPI.deletePromptRegistry;

        await apiMethod(itemToDelete.item.id);
        setDeleteDialogOpen(false);

        // Get current data and pagination state for this tab
        const tabKey = getTabKey(itemToDelete.type);
        const currentData = itemToDelete.type === 'model_registry' ? modelRegistry : promptRegistry;
        const currentPagination = paginationState[tabKey];

        // If current page becomes empty after deletion, go to previous page
        if (currentData.length === 1 && currentPagination.page > 0) {
          setPaginationState(prev => ({
            ...prev,
            [tabKey]: { ...prev[tabKey], page: currentPagination.page - 1 }
          }));
        } else {
          loadAllData();
        }

        setItemToDelete(null);
        setSuccess('Record deleted successfully');
      } catch (error) {
        console.error('Failed to delete item:', error);
        const errorMessage = error.response?.data?.error || `Failed to delete ${itemToDelete.type.replace('_', ' ')}`;
        setError(errorMessage);
        setDeleteDialogOpen(false);
        setItemToDelete(null);
      }
    }
  };

  const handleEdit = async (item, type) => {
    setEditingItem(item);
    setDialogType(type);
    setError('');
    setValidationErrors({});

    // Normalize model_compatibility to always be an array
    const normalizedItem = { ...item };
    if (type === 'prompt_registry' && normalizedItem.model_compatibility) {
      if (typeof normalizedItem.model_compatibility === 'string') {
        // Handle legacy comma-separated string format
        normalizedItem.model_compatibility = normalizedItem.model_compatibility
          .split(',')
          .map(m => m.trim())
          .filter(m => m);
      } else if (!Array.isArray(normalizedItem.model_compatibility)) {
        // Fallback for any other format
        normalizedItem.model_compatibility = [];
      }
    } else if (type === 'prompt_registry' && !normalizedItem.model_compatibility) {
      normalizedItem.model_compatibility = [];
    }

    setFormData(normalizedItem);

    // Set inputPrompt for editing
    if (type === 'prompt_registry' && normalizedItem.prompt_template) {
      setInputPrompt(normalizedItem.prompt_template);
    } else {
      setInputPrompt('');
    }

    // Handle dictionary_data for editing - ensure it's properly formatted JSON string
    if (type === 'prompt_registry' && normalizedItem.dictionary_data) {
      console.log('🔍 Original dictionary_data:', normalizedItem.dictionary_data);
      console.log('🔍 Type of dictionary_data:', typeof normalizedItem.dictionary_data);

      if (typeof normalizedItem.dictionary_data === 'object') {
        // If it's already an object, format it nicely
        normalizedItem.dictionary_data = JSON.stringify(normalizedItem.dictionary_data, null, 2);
        console.log('🔍 Formatted object to JSON:', normalizedItem.dictionary_data);
      } else if (typeof normalizedItem.dictionary_data === 'string') {
        // If it's a string, try to parse and re-format it for better display
        try {
          const parsed = JSON.parse(normalizedItem.dictionary_data);
          normalizedItem.dictionary_data = JSON.stringify(parsed, null, 2);
          console.log('🔍 Parsed and reformatted string:', normalizedItem.dictionary_data);
        } catch (e) {
          // If parsing fails, keep the original string
          console.warn('Failed to parse dictionary_data JSON:', e);
        }
      }

      // Always ensure the formData gets the properly formatted JSON
      setFormData(prev => ({ ...prev, dictionary_data: normalizedItem.dictionary_data }));
    } else {
      console.log('🔍 No dictionary_data found');
      // Ensure dictionary_data is cleared if not present
      setFormData(prev => ({ ...prev, dictionary_data: '' }));
    }

    // If editing a prompt registry item, load all model types
    if (type === 'prompt_registry') {
      await loadModelTypes();
    }

    setDialogOpen(true);
  };

  const handleDuplicate = (item, type) => {
    // Normalize model_compatibility to always be an array
    const normalizedItem = { ...item };
    if (type === 'prompt_registry' && normalizedItem.model_compatibility) {
      if (typeof normalizedItem.model_compatibility === 'string') {
        normalizedItem.model_compatibility = normalizedItem.model_compatibility
          .split(',')
          .map(m => m.trim())
          .filter(m => m);
      } else if (!Array.isArray(normalizedItem.model_compatibility)) {
        normalizedItem.model_compatibility = [];
      }
    } else if (type === 'prompt_registry' && !normalizedItem.model_compatibility) {
      normalizedItem.model_compatibility = [];
    }

    // Pre-fill form data for duplication
    const duplicateData = {
      ...normalizedItem,
      name: `${normalizedItem.name}_copy`,
      display_name: `${normalizedItem.display_name} (Copy)`,
      is_active: false // New copy should initially be inactive
    };

    // Remove ID and other metadata that should not be copied to a new entry
    delete duplicateData.id;
    delete duplicateData.created_at;
    delete duplicateData.updated_at;
    delete duplicateData.created_by;
    delete duplicateData.updated_by;

    setFormData(duplicateData);
    setDialogType(type);
    setEditingItem(null); // Ensure it's treated as a new entry
    setError('');
    setValidationErrors({});

    // Set inputPrompt for duplication
    if (type === 'prompt_registry' && normalizedItem.prompt_template) {
      setInputPrompt(normalizedItem.prompt_template);
    } else {
      setInputPrompt('');
    }

    // Handle dictionary_data for duplication
    if (type === 'prompt_registry' && normalizedItem.dictionary_data) {
      if (typeof normalizedItem.dictionary_data === 'object') {
        const formattedData = JSON.stringify(normalizedItem.dictionary_data, null, 2);
        setFormData(prev => ({ ...prev, dictionary_data: formattedData }));
      } else if (typeof normalizedItem.dictionary_data === 'string') {
        try {
          const parsed = JSON.parse(normalizedItem.dictionary_data);
          const formattedData = JSON.stringify(parsed, null, 2);
          setFormData(prev => ({ ...prev, dictionary_data: formattedData }));
        } catch (e) {
          // Keep as is if parsing fails
          setFormData(prev => ({ ...prev, dictionary_data: normalizedItem.dictionary_data }));
        }
      }
    }

    setDialogOpen(true);
  };

  const handleNew = (type) => {
    setDialogType(type);
    setEditingItem(null);
    setError('');
    setValidationErrors({});
    resetFormData();

    setDialogOpen(true);
  };

  const resetFormData = () => {
    setFormData({
      name: '',
      display_name: '',
      description: '',
      is_active: true,
      sort_order: 0,
      // Prompt registry specific fields
      prompt_template: '',
      category: '',
      prompt_type: '',
      model_compatibility: [],
      gender_option_id: '',

      // New field for prompt
      prompt_template: '',
      // Dictionary data field
      dictionary_data: '',
      // Generation parameters
      guidance_scale: '',
      // Model registry specific fields
      version: '',
      api_endpoint: '',
      provider: '',
      credits: '',
      model_type: '',
      // Conditional fields based on model type
      guidance_scale: '',
      safety_tolerance: '',
      duration: '',
      seed: '',
    });

    // Reset inputPrompt
    setInputPrompt('');
  };

  // Pagination handlers
  const handlePageChange = (type, newPage) => {
    const tabKey = getTabKey(type);
    setPaginationState(prev => ({
      ...prev,
      [tabKey]: { ...prev[tabKey], page: newPage }
    }));
  };

  const handleRowsPerPageChange = (type, newRowsPerPage) => {
    const tabKey = getTabKey(type);
    setPaginationState(prev => ({
      ...prev,
      [tabKey]: { ...prev[tabKey], page: 0, rowsPerPage: newRowsPerPage }
    }));
  };

  const renderDataTable = (data, type) => {
    const tabKey = getTabKey(type);
    const pagination = paginationState[tabKey];
    const totalCount = totalCounts[tabKey];

    const tabData = getTabData(activeTab);
    const columns = tabData.columns || [];

    if (!pagination) {
      return (
        <Box p={3} textAlign="center">
          <CircularProgress />
          <Typography>Loading pagination data...</Typography>
        </Box>
      );
    }

    if (!data || !Array.isArray(data) || data.length === 0) {
      return (
        <Box p={3} textAlign="center">
          <Typography>No {type.replace('_', ' ')} found</Typography>
        </Box>
      );
    }

    return (
      <>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.id}>{column.label}</TableCell>
                ))}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{ backgroundColor: item.is_model_face ? '#f5f5f5' : 'inherit' }}
                >
                  {columns.map((column) => (
                    <TableCell key={column.id}>
                      {column.type === 'boolean' ? (
                        <Chip
                          label={item[column.id] ? 'Active' : 'Inactive'}
                          color={item[column.id] ? 'success' : 'error'}
                          size="small"
                        />
                      ) : column.format ? (
                        <Typography variant="body2">
                          {column.format(item[column.id], item)}
                        </Typography>
                      ) : (
                        <Typography variant="body2">
                          {item[column.id] || '-'}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                  <TableCell>
                    <Box display="flex" gap={1}>
                      {canUpdate && (
                        <Tooltip title="Edit">
                          <span> {/* Wrapper needed for Tooltip to work with disabled button */}
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(item, type)}
                            // Removed disabled condition - now edit is enabled for all types including prompt_registry
                            >
                              <Edit />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {type === 'prompt_registry' && canCreate && (
                        <Tooltip title="Duplicate">
                          <IconButton
                            size="small"
                            onClick={() => handleDuplicate(item, type)}
                          >
                            <ContentCopy sx={{ fontSize: '1.2rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(item, type)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={pagination.page}
          onPageChange={(e, newPage) => handlePageChange(type, newPage)}
          rowsPerPage={pagination.rowsPerPage}
          onRowsPerPageChange={(e) => handleRowsPerPageChange(type, parseInt(e.target.value, 10))}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </>
    );
  };

  if (!canView) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          You don't have permission to view Middleware Data.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" gutterBottom>
            Middleware Data
          </Typography>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={loadAllData}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
        >
          <Tab
            label="Model Registry"
            icon={<Memory />}
            iconPosition="start"
          />
          <Tab
            label="Prompt Registry"
            icon={<Psychology />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Content */}
      <Paper elevation={2} sx={{ p: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography variant="h6">
                {getTabData(activeTab).title}
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <FormControlLabel
                  sx={{
                    ml: 0,
                    '& .MuiSwitch-root': { mr: 1 }
                  }}
                  control={
                    <Switch
                      checked={showInactiveOnly}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setShowInactiveOnly(newValue);
                        // Save the preference to localStorage
                        localStorage.setItem('middlewareData_showInactiveOnly', JSON.stringify(newValue));
                      }}
                      color="primary"
                    />
                  }
                  label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Show Inactive Only</Typography>}
                />
                {canCreate && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleNew(getTabData(activeTab).type)}
                  >
                    New {getTabData(activeTab).type === 'model_registry' ? 'Model Registry' : 'Prompt Registry'}
                  </Button>
                )}
              </Box>
            </Box>

            {activeTab === 1 && (
              <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', p: 2, bgcolor: '#f9fafb', borderRadius: 2, border: '1px solid #e5e7eb' }}>
                <TextField
                  size="small"
                  label="Search Prompts"
                  placeholder="Search by name or display name..."
                  value={promptFilters.search}
                  onChange={(e) => setPromptFilters(prev => ({ ...prev, search: e.target.value }))}
                  sx={{ width: { xs: '100%', sm: 300 } }}
                />

                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Model Type</InputLabel>
                  <Select
                    value={promptFilters.model_type_id}
                    label="Model Type"
                    onChange={(e) => setPromptFilters(prev => ({ ...prev, model_type_id: e.target.value }))}
                  >
                    <MenuItem value=""><em>All Model Types</em></MenuItem>
                    {modelTypes.map(type => (
                      <MenuItem key={type.id} value={type.id}>{type.display_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Model Face</InputLabel>
                  <Select
                    value={promptFilters.is_model_face}
                    label="Model Face"
                    onChange={(e) => setPromptFilters(prev => ({ ...prev, is_model_face: e.target.value }))}
                  >
                    <MenuItem value=""><em>Both</em></MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>

                {(promptFilters.search || promptFilters.model_type_id || promptFilters.is_model_face) && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={() => setPromptFilters({ search: '', model_type_id: '', is_model_face: '' })}
                  >
                    Clear Filters
                  </Button>
                )}
              </Box>
            )}

            {renderDataTable(getTabData(activeTab).data, getTabData(activeTab).type)}
          </>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => {
        setDialogOpen(false);
        setError(''); // Clear error when dialog closes
        setValidationErrors({}); // Clear validation errors when dialog closes
      }} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingItem ?
            `Edit ${dialogType.replace('_', ' ')}` :
            `New ${dialogType.replace('_', ' ')}`}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {renderFormFields(dialogType)}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDialogOpen(false);
            setError(''); // Clear error when dialog closes
            setValidationErrors({}); // Clear validation errors when dialog closes
          }}>Cancel</Button>
          <Button
            onClick={editingItem ? handleUpdate : handleCreate}
            variant="contained"
          >
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          color: 'error.main',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Delete />
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '1.1rem', color: 'text.primary' }}>
            Are you sure you want to delete{' '}
            <strong>{itemToDelete?.item?.display_name || itemToDelete?.item?.name}</strong>?
          </Typography>
          <Typography sx={{ mt: 2, color: 'text.secondary' }}>
            This action cannot be undone. The item will be permanently removed from the system.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            size="large"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            size="large"
            sx={{ minWidth: 100 }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MiddlewareData; 